import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import {
  authService,
  adminAuthService,
  usersService,
  apiClient,
  adminApiClient,
  AUTH_SESSION_EXPIRED_EVENT,
} from '../services/api'

const AuthContext = createContext(null)

const VIEW_MODE_KEY = 'proconnect_view_mode'
const USER_KEY = 'proconnect_user'

const PRO_MODULE_KEYS = new Set([
  'artisan_profile_access',
  'artisan_directory_visibility',
  'request_response_access',
  'job_offer_publisher',
  'kyc_submission_access',
  'verified_badge_access',
])

const parseName = (name = '') => {
  const parts = name.trim().split(/\s+/)
  return {
    prenom: parts[0] || '',
    nom: parts.slice(1).join(' ') || '',
  }
}

const mapKycStatus = (kycDocuments = [], proStatus) => {
  if (kycDocuments.some((doc) => doc.status === 'APPROVED')) return 'valide'
  if (kycDocuments.some((doc) => doc.status === 'PENDING')) return 'en_attente'
  if (proStatus === 'PENDING') return 'en_attente_validation'
  return 'non_soumis'
}

const buildModules = (proStatus, kycValidated) => ({
  basic_access: true,
  request_publisher: true,
  artisan_profile_access: proStatus === 'APPROVED',
  artisan_directory_visibility: proStatus === 'APPROVED',
  request_response_access: proStatus === 'APPROVED',
  job_offer_publisher: proStatus === 'APPROVED',
  kyc_submission_access: proStatus === 'APPROVED',
  verified_badge_access: kycValidated,
})

export const normalizeUser = (rawUser) => {
  if (!rawUser) return null

  const data = rawUser.data ?? rawUser
  const { prenom, nom } = parseName(
    data.name || `${data.prenom || ''} ${data.nom || ''}`.trim()
  )
  const kycStatus = mapKycStatus(data.kycDocuments, data.proStatus)
  const isProApproved = data.proStatus === 'APPROVED'
  const kycValidated = kycStatus === 'valide'

  return {
    ...data,
    prenom: data.prenom || prenom,
    nom: data.nom || nom,
    account_type: isProApproved ? 'pro' : 'client',
    viewMode: data.viewMode === 'pro' ? 'pro' : 'particulier',
    pro_status: isProApproved
      ? 'actif'
      : data.proStatus === 'PENDING'
        ? 'en_attente'
        : 'non_demande',
    photo: data.photoUrl ?? data.photo,
    ville: data.city ?? data.ville,
    telephone: data.phone ?? data.telephone,
    kyc_status: kycStatus,
    status: data.accountStatus === 'ACTIVE' ? 'actif' : data.accountStatus?.toLowerCase(),
    profile_completed: !!(data.name && (data.city || data.phone)),
    subscription_status: data.subscription?.status === 'ACTIVE' ? 'actif' : 'inactif',
    modules: buildModules(data.proStatus, kycValidated),
  }
}

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (!raw) return null
    return normalizeUser(JSON.parse(raw))
  } catch {
    return null
  }
}

const persistUser = (userData) => {
  if (userData) {
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
  } else {
    localStorage.removeItem(USER_KEY)
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readStoredUser())
  const [viewMode, setViewModeState] = useState(() =>
    localStorage.getItem(VIEW_MODE_KEY) === 'pro' ? 'pro' : 'particulier'
  )
  const [viewModeLoading, setViewModeLoading] = useState(false)

  const [admin, setAdmin] = useState(null)

  const [loading, setLoading] = useState(true)

  const getAuthToken = useCallback(() => {
    if (user) return 'cookie-session'
    if (readStoredUser()) return 'cookie-session'
    return null
  }, [user])

  const clearSession = useCallback(() => {
    localStorage.removeItem(VIEW_MODE_KEY)
    persistUser(null)
    setUser(null)
    setViewModeState('particulier')
  }, [])

  useEffect(() => {
    const handleSessionExpired = () => {
      clearSession()
    }

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired)
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired)
  }, [clearSession])

  const syncViewMode = useCallback((mode) => {
    const nextMode = mode === 'pro' ? 'pro' : 'particulier'
    setViewModeState(nextMode)
    localStorage.setItem(VIEW_MODE_KEY, nextMode)
  }, [])

  const setViewMode = useCallback(async (mode) => {
    const nextMode = mode === 'pro' ? 'pro' : 'particulier'
    const previousMode = localStorage.getItem(VIEW_MODE_KEY) === 'pro' ? 'pro' : 'particulier'

    if (!user) {
      syncViewMode(nextMode)
      return
    }

    syncViewMode(nextMode)
    setViewModeLoading(true)

    try {
      await usersService.updateViewMode(nextMode)
    } catch (error) {
      console.error('Error updating view mode:', error)
      try {
        await usersService.updateViewMode(nextMode)
      } catch (retryError) {
        console.error('Retry updating view mode failed:', retryError)
        syncViewMode(previousMode)
        throw retryError
      }
    } finally {
      setViewModeLoading(false)
    }
  }, [syncViewMode, user])

  const fetchUser = useCallback(async ({ manageLoading = false } = {}) => {
    if (manageLoading) setLoading(true)

    try {
      const response = await usersService.getMyProfile()
      const userData = normalizeUser(response.data ?? response)
      setUser(userData)
      persistUser(userData)

      const apiViewMode = userData?.viewMode === 'pro' ? 'pro' : 'particulier'
      syncViewMode(apiViewMode)

      return userData
    } catch (error) {
      if (error.status === 401) {
        clearSession()
        return null
      }
      console.error('Error fetching user:', error)
      clearSession()
      return null
    } finally {
      if (manageLoading) setLoading(false)
    }
  }, [clearSession, syncViewMode])

  const fetchAdmin = useCallback(async () => {
    try {
      const response = await adminAuthService.getCurrentAdmin()
      const adminData = response.data || response
      setAdmin(adminData)
      localStorage.setItem('admin_data', JSON.stringify(adminData))
      return adminData
    } catch (error) {
      console.error('Error fetching admin:', error)
      localStorage.removeItem('admin_data')
      setAdmin(null)
      return null
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const bootstrapSession = async () => {
      setLoading(true)
      const tasks = [fetchUser()]
      const shouldLoadAdmin =
        localStorage.getItem('admin_data') ||
        window.location.pathname.startsWith('/admin')
      if (shouldLoadAdmin) {
        tasks.push(fetchAdmin())
      }
      await Promise.allSettled(tasks)
      if (!cancelled) setLoading(false)
    }

    bootstrapSession()

    return () => {
      cancelled = true
    }
  }, [fetchUser, fetchAdmin])

  const login = async (email, password, rememberMe = true) => {
    const response = await authService.login(email, password, rememberMe)
    await fetchUser()
    return { success: true, data: response }
  }

  const register = async (userData) => {
    const response = await authService.register(userData)
    await fetchUser()
    return { success: true, data: response }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearSession()
    }
  }

  const adminLogin = async (email, password, rememberMe = true) => {
    const response = await adminAuthService.login(email, password, rememberMe)
    const adminData = response.data?.user || response.user
    setAdmin(adminData)
    if (adminData) {
      localStorage.setItem('admin_data', JSON.stringify(adminData))
    }
    return { success: true, data: response }
  }

  const adminLogout = async () => {
    try {
      await adminAuthService.logout()
    } catch (error) {
      console.error('Admin logout error:', error)
    } finally {
      localStorage.removeItem('admin_data')
      setAdmin(null)
    }
  }

  const fullLogout = async () => {
    await logout()
    await adminLogout()
  }

  const updateProfile = async (formData) => {
    await usersService.updateMyProfile(formData)
    return fetchUser()
  }

  const refreshUser = async () => fetchUser({ manageLoading: true })

  const activatePro = async () => {
    const response = await usersService.activatePro()
    await fetchUser()
    return response?.data ?? response
  }

  const hasModule = (moduleName) => {
    if (PRO_MODULE_KEYS.has(moduleName) && viewMode !== 'pro') {
      return false
    }
    return user?.modules?.[moduleName] === true
  }

  const isAdmin = () => Boolean(admin)

  const hasPermission = (permissionName) => {
    return admin?.permissions?.[permissionName] === true
  }

  const isAdminRole = (role) => {
    return admin?.role === role
  }

  const isPro = () => {
    return user?.proStatus === 'APPROVED' ||
           (user?.account_type === 'pro' && user?.pro_status === 'actif')
  }

  const isProView = () => {
    return viewMode === 'pro' && isPro()
  }

  const hasK6Access = () => {
    return user?.assistanceK6Access === true
  }

  const isAuthenticated = useMemo(() => Boolean(user), [user])

  const token = useMemo(() => {
    if (user) return 'cookie-session'
    if (readStoredUser()) return 'cookie-session'
    return null
  }, [user])

  const value = {
    user,
    loading,
    isAuthenticated,
    token,
    getAuthToken,
    viewMode,
    viewModeLoading,
    setViewMode,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
    activatePro,
    hasModule,
    isPro,
    isProView,
    hasK6Access,
    axiosInstance: apiClient,

    admin,
    adminLogin,
    adminLogout,
    isAdmin,
    hasPermission,
    isAdminRole,
    adminAxios: adminApiClient,

    fullLogout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
